'use client';

import { useAuth } from '@/app/hooks/useAuth';
import { useMicrosoftGraph } from '@/app/hooks/useMicrosoftGraph';
import { Box, Typography, Menu, MenuItem, Avatar } from '@mui/material';
import { Session } from 'next-auth';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';

interface UserProfileProps {
  user: Session['user'] | null;
  anchorEl: HTMLElement | null;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onMenuClose: () => void;
  onLogout: () => void;
}

export default function UserProfile({
  user,
  anchorEl,
  onMenuOpen,
  onMenuClose,
  onLogout
}: UserProfileProps) {
  const { provider } = useAuth();
  const { getUserPhoto } = useMicrosoftGraph();

  const displayName = user?.name || user?.email || 'User';
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const lastObjectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    if (provider !== 'azure-ad') return;
    (async () => {
      try {
        const photo = await getUserPhoto('96x96');
        if (active && photo) {
          if (lastObjectUrlRef.current && lastObjectUrlRef.current.startsWith('blob:')) {
            URL.revokeObjectURL(lastObjectUrlRef.current);
          }
          lastObjectUrlRef.current = photo;
          setPhotoUrl(photo);
        }
      } catch { /* fallback to letter avatar */ }
    })();
    return () => {
      active = false;
      if (lastObjectUrlRef.current && lastObjectUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(lastObjectUrlRef.current);
      }
    };
  }, [provider, getUserPhoto]);

  if (!user) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          borderRadius: '20px',
          padding: '4px 12px 4px 16px',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          },
          transition: 'all 0.2s ease'
        }}
        onClick={onMenuOpen}
      >
        <Typography
          color="white"
          sx={{
            fontWeight: 500,
            marginRight: 1
          }}
        >
          {displayName}
        </Typography>
        <Avatar
          src={photoUrl || undefined}
          alt={displayName}
          sx={{ width: 36, height: 36, bgcolor: '#3b82f6', fontSize: '14px' }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
      </Box>
      <UserMenu
        anchorEl={anchorEl}
        onMenuClose={onMenuClose}
        onLogout={onLogout}
      />
    </>
  );
}

interface UserMenuProps {
  anchorEl: HTMLElement | null;
  onMenuClose: () => void;
  onLogout: () => void;
}

function UserMenu({ anchorEl, onMenuClose, onLogout }: UserMenuProps) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            minWidth: 180,
          }
        }
      }}
    >
      <MenuItem>
        <Link href="/user" style={{ textDecoration: 'none', color: 'inherit', width: '100%' }} onClick={onMenuClose}>
          Profile
        </Link>
      </MenuItem>
      <MenuItem
        onClick={() => {
          onMenuClose();
          onLogout();
        }}
        sx={{
          color: 'error.main'
        }}
      >
        Sign Out
      </MenuItem>
    </Menu>
  );
}